<template>
  <div>
    <Pagination
      :loader-size="30"
      :is-fetching="isFetchingMore"
      :total="total"
      :page="page"
      @fetchMore="handleFetchMore"
    >
      <v-list two-line>
        <v-list-item-group
          v-if="items && items.length"
          multiple
        >
          <template v-for="(item, index) in items">
            <v-list-item
              :v-if="item"
              :key="item.id"
              inactive
              @click="(isAdmin || option.type == 'connections') ? viewUser(item.id) : null"
              class="pa-0 px-2 pt-0"
              :ripple="isAdmin || option.type == 'connections'"
            >
              <v-list-item-avatar
                width="62"
                height="62"
                class="mr-3"
              >
                <ProfilePic
                  v-if="!!item.picture"
                  :src="item.picture"
                  :width="62"
                />
              </v-list-item-avatar>

              <v-list-item-content align="top">
                <v-list-item-title class="subtitle-1 mb-0 pb-0 no-ellipsis">
                  <strong>{{ item.name }}</strong>
                  <UserAchievements
                    v-if="!$mediaQuery.isExtraSmall"
                    class="ml-2"
                    :user="item"
                  />
                </v-list-item-title>

                <v-list-item-subtitle class="role subtitle-1 ma-0 mb-1">
                  {{ item.role }}
                </v-list-item-subtitle>

                <v-list-item-subtitle class="location">
                  {{ item.location }}
                </v-list-item-subtitle>

                <v-list-item-subtitle
                  v-if="$mediaQuery.isExtraSmall && item.achievements && item.achievements.length"
                >
                  <UserAchievements
                    class="mt-2"
                    :user="item"
                  />
                </v-list-item-subtitle>


                <v-list-item-subtitle class="grey--text">
                  <v-row>
                    <ItemsNumberCol
                      :items-number="item.opportunitesPublishedNumber"
                      :icon="require('@/assets/images/ui/opportunity-share-icon.svg')"
                      max-width="25"
                      singular-label="opportunity"
                      plural-label="opportunities"
                    />
                    <ItemsNumberCol
                      :items-number="item.referralsNumber"
                      :icon="require('@/assets/images/ui/referrals-icon.svg')"
                      max-width="22"
                      singular-label="referral"
                    />
                    <ItemsNumberCol
                      :items-number="item.connectionsNumber"
                      :icon="require('@/assets/images/ui/bottom-nav/network.svg')"
                      singular-label="connection"
                    />
                  </v-row>
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <div class="text-center">
                  <div v-if="option.type == 'connections'">
                    <v-btn
                      class="mr-3"
                      v-show="item.phone"
                      @click="phoneUser(item)"
                      fab
                      dark
                      x-small
                      color="secondary"
                    >
                      <v-icon dark>
                        phone
                      </v-icon>
                    </v-btn>

                    <v-btn
                      v-show="item.email"
                      @click="emailUser(item)"
                      fab
                      dark
                      x-small
                      color="secondary"
                    >
                      <v-icon dark>
                        email
                      </v-icon>
                    </v-btn>
                  </div>

                  <div v-else-if="option.type == 'network'">
                    <template v-if="profileID !== item.id">
                      <v-btn
                        v-if="item.isConnectedToMe"
                        color="secondary"
                        light
                        rounded
                        outlined
                        small
                        disabled
                      >
                        Connected
                      </v-btn>
                      <v-btn
                        v-else-if="isInvitePending(item)"
                        color="secondary"
                        light
                        rounded
                        outlined
                        small
                        disabled
                      >
                        Invite Pending
                      </v-btn>
                      <v-btn
                        v-else-if="isInviteSent(item)"
                        color="secondary"
                        light
                        rounded
                        outlined
                        small
                        disabled
                      >
                        Invite Sent
                      </v-btn>

                      <template v-else>
                        <v-btn
                          dark
                          rounded
                          outlined
                          small
                          color="primary"
                          @click.stop="createInvite(item.id)"
                        >
                          Connect
                        </v-btn>
                      </template>
                    </template>
                  </div>

                  <div v-else-if="option.type == 'invites'">
                    <v-btn
                      dark
                      fab
                      x-small
                      color="secondary"
                      class="mr-3"
                      @click="acceptInvite(item.id)"
                    >
                      <v-icon dark>
                        check
                      </v-icon>
                    </v-btn>

                    <v-btn
                      dark
                      fab
                      x-small
                      color="secondary"
                      @click="declineInvite(item.id)"
                    >
                      <v-icon dark>
                        close
                      </v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-list-item-action>
            </v-list-item>

            <v-divider
              v-if="index + 1 < items.length"
              :key="index"
            />
          </template>
        </v-list-item-group>
        <v-list-item-group
          v-else-if="isSearching && !isFetchingMore && !items.length"
          class="text-center mt-5"
        >
          <i class="body-1">There are no results for this search</i>
        </v-list-item-group>
      </v-list>
    </Pagination>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import { mapGetters } from 'vuex'

import ProfilePic from '@/components/core/ProfilePic.vue'
import Pagination from '@/components/core/Pagination.vue'
import ItemsNumberCol from '@/components/connections/ItemsNumberCol.vue'
import UserAchievements from '@/components/achievements/UserAchievements.vue'
import { ISimpleInvite, IUser } from '@/types/models/user.model'
import { IPagination } from '@/types/api'
import { trackConnectUserBtnTap, inviteEvents, userProfileEvents } from '@/segmentAnalytics/connections'

interface IOptionProp {
  type: 'connections'|'network'|'invites'
}

@Component({
  components: {
    ProfilePic,
    Pagination,
    ItemsNumberCol,
    UserAchievements
  },
  computed: {
    ...mapGetters([
      'profileID',
      'isAdmin',
      'invites',
      'connections',
    ])
  },
})
export default class ListConnections extends Vue {
  @Prop({ required: true }) items: IUser[]
  @Prop({ required: true }) option: IOptionProp
  @Prop({ required: true }) isFetchingMore: boolean
  @Prop({ required: true }) total: number
  @Prop({ default: false }) isSearching: boolean
  @Prop() page: number

  // data
  disabledView: boolean = false
  inviteSentTo: { [key: number]: boolean } = {}

  // computed
  profileID: number
  isAdmin: boolean
  invites: ISimpleInvite[]
  connections: IUser[]

  // methods
  phoneUser (user: IUser) {
    userProfileEvents.trackPhoneTap(user.id)
    this.disabledView = true
    // console.log('phone user: ', phone)
    window.location.href = `tel:${user.phone}`
  }
  emailUser (user: IUser) {
    userProfileEvents.trackEmailTap(user.id)
    this.disabledView = true
    // console.log('email user: ', email)
    window.location.href= `mailto:${user.email}`
  }
  viewUser (id: number) {
    if (!this.disabledView) {
      this.$emit('showUser', id)
    }
    this.disabledView = false
  }
  createInvite (id: number) {
    this.inviteSentTo = { ...this.inviteSentTo, [id]: true }
    trackConnectUserBtnTap()
    this.$emit('createInvite', id)
  }
  acceptInvite (id: number) {
    inviteEvents.trackAccept()
    this.disabledView = true
    this.$emit('acceptInvite', id)
  }
  declineInvite (id: number) {
    inviteEvents.trackDecline()
    this.disabledView = true
    this.$emit('declineInvite', id)
  }
  handleFetchMore ($event: IPagination) {
    this.$emit('fetchMore', $event)
  }
  isInviteSent (userItem?: IUser) {
    if (!userItem) return false
    return !!this.invites?.find(invite => invite?.to === userItem?.id) || !!this.inviteSentTo[userItem.id]
  }
  isInvitePending (userItem?: IUser) {
    if (!userItem) return false
    return !!this.invites?.find(invite => {
      return invite?.from === userItem?.id && invite?.to === this.profileID
    })
  }
}
</script>
<style lang="scss" scoped>
.role,
.location {
  color: grey;
}
.location {
  font-size: 0.8em;
}
.no-ellipsis {
  white-space: normal;
  text-overflow: initial;
}
</style>
